import type { GamesPanelCopy } from "../components/GamesPanel";
import type { Localized } from "../i18n";

/**
 * The words the per-game panel needs.
 *
 * 🔴 ONE DICTIONARY FOR FOUR TIERS, AND THAT IS THE SAME DECISION AS THE PANEL
 * ITSELF. `GamesPanel` deliberately emits no copy of its own — `formatDelta`'s
 * lesson: *"a formatter that emits user-facing copy cannot be localised"* — but
 * four tiers each writing their own "Rounds" and "Last played" is four chances
 * for the school dashboard and the class panel underneath it to disagree about
 * what a column means.
 *
 * ⚠️ THE TITLE AND THE HINT ARE **NOT** HERE. They are the one part that
 * genuinely differs per tier: a parent reads *"what {child} has been playing"*,
 * an operator reads *"across every school, last 30 days"*. Each tier supplies
 * those two and spreads the rest.
 *
 * ⚠️ AND NOTHING HERE NAMES A GAME. `GAME_TITLES` owns that, in one place, for
 * the reason it records at length.
 */
export const GAMES_PANEL_COPY: Localized<
  Omit<GamesPanelCopy, "title" | "hint">
> = {
  en: {
    game: "Game",
    rounds: "Rounds",
    children: "Children",
    /* ⚠️ "Right answers", not "Score". A score implies a ranking, and the
       parent tier refuses comparisons on principle — the same words reach it. */
    accuracy: "Right answers",
    level: "Level reached",
    lastPlayed: "Last played",

    gamesPlayed: "Games started",
    totalRounds: "Rounds finished",
    overallAccuracy: "Right answers",
    mostPlayed: "Played most",

    /* 🔴 A LEVEL READS AS A LEVEL — design-system.md §8. The denominator comes
       from `LEVEL_COUNT`, never from a number typed here. */
    levelTemplate: "Level {level} of {of}",
    notStarted: "Not started",
    /* 🔴 THE EMPTY STATE IS A SENTENCE PLUS AN ACTION, never a blank table. */
    nothingYet:
      "Nothing has been played yet. The games appear here after the first round.",
    ofTotal: (n: number, total: number) => `${String(n)} of ${String(total)}`,
    never: "Never",
  },
};
